import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { fmtDate as fmtMMDDYY } from '@/lib/dates';
import {
  Building2, Truck, FileText, AlertTriangle, ClipboardCheck, ChevronRight,
  ArrowRight, CalendarClock,
} from 'lucide-react';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const fmtDate = (d) => fmtMMDDYY(d) || '—';

const monthOptions = () => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 12; i += 1) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push({ value: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`, label: dt.toLocaleString('en-US', { month: 'long', year: 'numeric' }) });
  }
  return out;
};

// Top summary tiles — restrained accents, numbers are the focal point.
const StatTile = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4" data-testid={testid}>
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: `${accent}1A`, color: accent }}>
      <Icon size={20} />
    </span>
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="text-[30px] font-extrabold leading-none text-[#0E2A47]" style={value > 0 && accent === '#DC2626' ? { color: '#DC2626' } : undefined}>{value}</span>
        {sub && <span className="text-[11px] text-slate-400">{sub}</span>}
      </div>
    </div>
  </div>
);

const CoverageRow = ({ label, done, total }) => {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div className="py-2.5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-600">{label}</span>
        <span className="text-sm font-bold text-[#0E2A47]">{done} <span className="text-slate-400">of {total}</span></span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-[#8FC93A]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const StatusPill = ({ overdue, status }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${overdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
    {overdue && <AlertTriangle size={11} />}{overdue ? 'Overdue' : (status || 'Pending')}
  </span>
);

const OperationsOverview = ({ onOpen }) => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [err, setErr] = useState(null);
  const [month, setMonth] = useState('');
  const opts = monthOptions();

  useEffect(() => {
    const q = month ? `?month=${month}` : '';
    Promise.all([
      axios.get(`${API}/operations/overview-summary${q}`, auth(token)),
      axios.get(`${API}/operations/coverage${q}`, auth(token)),
    ]).then(([s, c]) => {
      setData(s.data); setCoverage(c.data);
      if (!month) setMonth(s.data.period.month);
    }).catch(() => setErr('Unable to load the operational overview.'));
  }, [token, month]);

  const label = data?.period?.label || '';
  const propTarget = coverage?.required_monthly ?? coverage?.active_locations ?? 0;
  const propCovered = coverage?.covered ?? 0;

  const openPoas = () => onOpen('corrective-actions', 'open');

  return (
    <div data-testid="ops-overview">
      {/* Standardized overview banner — supplied green Operations artwork (TFSI icon)
          kept fully visible on the right; functional title on the white left area. */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="ops-overview-banner">
        <div className="relative h-[118px] sm:h-[148px]">
          <img src="/module-banners/operations-banner.webp" alt="" aria-hidden="true"
            className="absolute right-0 top-0 h-full w-auto max-w-none object-contain object-right" data-testid="ops-banner-img" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, #ffffff 0%, #ffffff 30%, rgba(255,255,255,0.72) 46%, rgba(255,255,255,0) 66%)' }} aria-hidden="true" />
          <div className="absolute inset-0 sm:hidden" style={{ background: 'linear-gradient(90deg, #ffffff 0%, #ffffff 52%, rgba(255,255,255,0.82) 64%, rgba(255,255,255,0) 84%)' }} aria-hidden="true" />
          <div className="relative z-10 flex h-full flex-col justify-center px-6">
            <h1 className="text-[24px] sm:text-[32px] leading-tight font-extrabold tracking-tight text-[#0E2A47]" data-testid="ops-overview-title">Operations</h1>
          </div>
        </div>
      </div>
      {/* Reporting-period selector relocated directly below the banner (was embedded in it). */}
      <div className="mt-4 flex items-center justify-end gap-2">
        <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Period (Miami/ET)</label>
        <select data-testid="ops-month-select" value={month} onChange={(e) => setMonth(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-[#0E2A47] shadow-sm outline-none focus:border-[#8FC93A]">
          {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {err && <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600" data-testid="ops-overview-error">{err}</div>}

      {!err && !data && (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-slate-100" />)}
        </div>
      )}

      {data && (
        <>
          {/* Monthly totals */}
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="ops-overview-stats">
            <StatTile testid="ops-stat-property" icon={Building2} label="Property inspections" value={data.property_inspections_month} sub="this month" accent="#4d7c0f" />
            <StatTile testid="ops-stat-vehicle" icon={Truck} label="Vehicle inspections" value={data.vehicle_inspections_month} sub="this month" accent="#2563EB" />
            <StatTile testid="ops-stat-open-poas" icon={FileText} label="Open POAs" value={data.open_poas} sub="total" accent="#D97706" />
            <StatTile testid="ops-stat-overdue" icon={AlertTriangle} label="Overdue items" value={data.overdue_items} sub="total" accent="#DC2626" />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Plans of action to complete */}
            <div className="rounded-2xl border border-slate-200 bg-white lg:col-span-2" data-testid="ops-overview-poa-list">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
                <h2 className="text-sm font-bold uppercase tracking-wide text-[#0E2A47]">Plans of action to complete</h2>
                <button onClick={openPoas} data-testid="ops-overview-viewall-poas" className="inline-flex items-center gap-1 text-xs font-semibold text-[#4d7c0f] hover:underline">
                  View all POAs <ArrowRight size={13} />
                </button>
              </div>
              {data.poas.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-slate-400">
                  <ClipboardCheck size={26} className="mx-auto mb-2 text-slate-300" />
                  No open plans of action. Nothing outstanding for this view.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-5 py-2.5">Location</th>
                        <th className="px-5 py-2.5">Action</th>
                        <th className="px-5 py-2.5 whitespace-nowrap">Due</th>
                        <th className="px-5 py-2.5">Status</th>
                        <th className="px-5 py-2.5"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.poas.slice(0, 8).map((p) => (
                        <tr key={p.id} className="hover:bg-slate-50" data-testid={`ops-poa-row-${p.id}`}>
                          <td className="px-5 py-3 font-medium text-[#0E2A47]">{p.location_name}</td>
                          <td className="px-5 py-3 text-slate-600"><span className="line-clamp-1 max-w-[260px]">{p.action}</span></td>
                          <td className={`px-5 py-3 whitespace-nowrap ${p.is_overdue ? 'font-semibold text-red-600' : 'text-slate-600'}`}>{fmtDate(p.due_date)}</td>
                          <td className="px-5 py-3"><StatusPill overdue={p.is_overdue} status={p.status} /></td>
                          <td className="px-5 py-3 text-right">
                            <button onClick={openPoas} data-testid={`ops-poa-open-${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#4d7c0f] hover:underline">
                              Open POA <ChevronRight size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* This month + upcoming */}
            <div className="space-y-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="ops-overview-thismonth">
                <h2 className="text-sm font-bold uppercase tracking-wide text-[#0E2A47]">This month</h2>
                <p className="mt-0.5 text-xs text-slate-400">{label}</p>
                <div className="mt-2 divide-y divide-slate-100">
                  <CoverageRow label="Property inspections" done={propCovered} total={propTarget} />
                  <CoverageRow label="Vehicle inspections" done={data.vehicle_inspections_month} total={data.vehicle_total} />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white" data-testid="ops-overview-upcoming">
                <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3.5">
                  <CalendarClock size={15} className="text-[#8FC93A]" />
                  <h2 className="text-sm font-bold uppercase tracking-wide text-[#0E2A47]">Upcoming work</h2>
                </div>
                {data.upcoming.length === 0 ? (
                  <div className="px-5 py-8 text-center text-xs text-slate-400">No dated work coming up.</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {data.upcoming.map((u) => (
                      <li key={u.id}>
                        <button onClick={openPoas} data-testid={`ops-upcoming-${u.id}`} className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left hover:bg-slate-50">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-[#0E2A47]">{u.action}</div>
                            <div className="truncate text-xs text-slate-400">{u.location_name} · POA</div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="text-[10px] uppercase tracking-wide text-slate-400">Due</div>
                            <div className="text-xs font-semibold text-[#0E2A47]">{fmtDate(u.due_date)}</div>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Workspaces — entrances, kept separate from the summaries above */}
          <div className="mt-8" data-testid="ops-overview-workspaces">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Workspaces</h2>
              <span className="text-xs text-slate-400">Go to your work areas to take action</span>
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* Open Inspections */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="ops-ws-inspections">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#8FC93A]/15 text-[#4d7c0f]"><ClipboardCheck size={20} /></span>
                    <div>
                      <button onClick={() => onOpen('inspections')} data-testid="ops-ws-open-inspections" className="inline-flex items-center gap-1 text-base font-extrabold text-[#0E2A47] hover:text-[#4d7c0f]">
                        Open Inspections <ArrowRight size={15} />
                      </button>
                      <div className="text-xs text-slate-500">Property inspections, vehicle checks, warehouse and equipment</div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button onClick={() => onOpen('inspections')} data-testid="ops-ws-tab-property" className="rounded-full bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0E2A47]/90">Property inspections</button>
                  <button onClick={() => onOpen('inspections')} data-testid="ops-ws-tab-vehicle" className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-[#0E2A47] hover:bg-slate-50">Vehicle inspections</button>
                  <span data-testid="ops-ws-tab-warehouse" className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-400">Warehouse inspections <em className="not-italic text-[10px] uppercase tracking-wide">Coming soon</em></span>
                  <span data-testid="ops-ws-tab-equipment" className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-400">Equipment inspections <em className="not-italic text-[10px] uppercase tracking-wide">Coming soon</em></span>
                </div>
              </div>

              {/* Open POAs */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="ops-ws-poas">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#D97706]/15 text-[#D97706]"><FileText size={20} /></span>
                  <div>
                    <button onClick={openPoas} data-testid="ops-ws-open-poas" className="inline-flex items-center gap-1 text-base font-extrabold text-[#0E2A47] hover:text-[#4d7c0f]">
                      Open POAs <ArrowRight size={15} />
                    </button>
                    <div className="text-xs text-slate-500">View and manage all plans of action</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OperationsOverview;
